import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Gift, MessageCircle, Copy, Share2 } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { MaskLines, Reveal } from "../components/Reveal";
import { LogoMark } from "../components/Logo";
import { IMG } from "../lib/i18n";
import { shareUrl } from "../lib/referral";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MINE = "gioia-my-ref";

const STEPS = [
  ["Crea il tuo codice", "Inserisci nome e cellulare: ricevi subito un codice personale, sempre lo stesso."],
  ["Condividilo", "Invialo alle amiche su WhatsApp con un tocco, oppure copia il link."],
  ["Lei prenota", "Prenotando dal tuo link il codice si inserisce da solo nel modulo."],
  ["Una sorpresa per entrambe", "Al suo primo trattamento l'aspetta una sorpresa, e una aspetta anche te al tuo prossimo appuntamento."],
];

const message = (code) =>
  `Ciao! Ti consiglio Gioia Centro Estetico a Catanzaro, mi trovo benissimo ✨ Prenota il tuo primo trattamento con il mio codice ${code}: c'è una sorpresa per entrambe! ${shareUrl(code)}`;

const CodeCard = ({ mine }) => {
  const link = shareUrl(mine.code);
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(label); } catch { toast.error("Copia non riuscita"); }
  };
  const native = async () => {
    try { await navigator.share({ title: "Gioia Centro Estetico", text: message(mine.code) }); } catch { /* cancelled */ }
  };
  return (
    <motion.div initial={{ opacity: 0, y: 30, rotateX: 12 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} data-testid="referral-code-card">
      <div className="relative overflow-hidden rounded-2xl bg-[#14110F] text-[#F6F2EC] p-8 sm:p-10 grain">
        <LogoMark className="absolute -right-8 -bottom-12 w-48 h-60 opacity-[0.12]" color="#F6F2EC" accent="#C9A66B" />
        <p className="overline text-[0.62rem] text-[#C9A66B]">Il tuo codice personale</p>
        <p className="mt-4 font-stencil text-4xl sm:text-5xl tracking-[0.18em]" data-testid="referral-code-value">{mine.code}</p>
        <p className="mt-4 text-sm text-[#d9d0c4]">Ciao {mine.name}, condividilo con chi vuoi: una sorpresa per entrambe.</p>
      </div>
      <div className="mt-5 grid sm:grid-cols-3 gap-3">
        <a href={`https://wa.me/?text=${encodeURIComponent(message(mine.code))}`} target="_blank" rel="noreferrer" data-testid="referral-whatsapp-btn"
          className="inline-flex justify-center items-center gap-2 rounded-full bg-[#14110F] text-[#F6F2EC] px-5 py-3.5 text-[0.7rem] tracking-[0.18em] uppercase transition-colors duration-500 hover:bg-[#8A6A3A]">
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
        <button type="button" onClick={() => copy(link, "Link copiato")} data-testid="referral-copy-btn"
          className="inline-flex justify-center items-center gap-2 rounded-full border border-[#14110F] px-5 py-3.5 text-[0.7rem] tracking-[0.18em] uppercase text-[#14110F] transition-colors duration-500 hover:bg-[#14110F] hover:text-[#F6F2EC]">
          <Copy className="w-4 h-4" /> Copia link
        </button>
        <button type="button" onClick={typeof navigator !== "undefined" && navigator.share ? native : () => copy(message(mine.code), "Messaggio copiato")} data-testid="referral-share-btn"
          className="inline-flex justify-center items-center gap-2 rounded-full border border-[#14110F] px-5 py-3.5 text-[0.7rem] tracking-[0.18em] uppercase text-[#14110F] transition-colors duration-500 hover:bg-[#14110F] hover:text-[#F6F2EC]">
          <Share2 className="w-4 h-4" /> Altro
        </button>
      </div>
    </motion.div>
  );
};

export default function Referral() {
  const [mine, setMine] = useState(() => { try { return JSON.parse(localStorage.getItem(MINE) || "null"); } catch { return null; } });
  const [f, setF] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); document.title = "Porta un'amica · Gioia Centro Estetico"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/referrals`, { ...f, email: f.email || null });
      const v = { code: data?.code, name: data?.name ?? f.name };
      if (!v.code) throw new Error("no code");
      localStorage.setItem(MINE, JSON.stringify(v));
      setMine(v);
    } catch {
      toast.error("Non è stato possibile creare il codice. Riprova.");
    } finally {
      setLoading(false);
    }
  };
  const field = "w-full bg-transparent border-0 border-b border-[#ddd4c7] py-3 text-[#14110F] outline-none focus:border-[#8A6A3A] transition-colors duration-300";

  return (
    <PageShell>
      <div className="max-w-[1440px] mx-auto" data-testid="referral-page">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="overline text-[#8A6A3A] flex items-center gap-3"><Gift className="w-4 h-4" strokeWidth={1.4} /> Porta un'amica</p>
            <h1 className="mt-6 font-serif-d font-light text-[3rem] sm:text-7xl leading-[0.95] text-[#14110F]" data-testid="referral-title">
              <MaskLines lines={["La bellezza"]} delay={0.1} />
              <MaskLines lines={["si condivide."]} delay={0.25} lineClass="italic text-[#8A6A3A]" />
            </h1>
            <Reveal delay={0.2}><p className="mt-8 text-[#4f4841] font-light leading-relaxed max-w-lg">Invita un'amica da Gioia: al suo primo trattamento riceve <strong className="font-medium text-[#14110F]">una sorpresa</strong>, e una sorpresa aspetta anche te.</p></Reveal>
            <div className="mt-12">
              {mine ? <CodeCard mine={mine} /> : (
                <Reveal delay={0.25}>
                  <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="referral-form">
                    <label className="block"><span className="overline text-[0.62rem] text-[#5a534c]">Il tuo nome</span>
                      <input required minLength={2} data-testid="referral-name-input" className={field} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
                    <label className="block"><span className="overline text-[0.62rem] text-[#5a534c]">Il tuo cellulare</span>
                      <input required minLength={5} type="tel" data-testid="referral-phone-input" className={field} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></label>
                    <label className="block sm:col-span-2"><span className="overline text-[0.62rem] text-[#5a534c]">Email (facoltativa)</span>
                      <input type="email" data-testid="referral-email-input" className={field} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
                    <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#4f4841] cursor-pointer">
                      <input type="checkbox" required data-testid="referral-consent-checkbox" className="mt-1 accent-[#8A6A3A] w-4 h-4" />
                      <span>Acconsento al trattamento dei dati per gestire il programma. <Link to="/privacy" className="underline underline-offset-4 decoration-[#8A6A3A]">Informativa privacy</Link></span>
                    </label>
                    <button type="submit" disabled={loading} data-testid="referral-submit-btn" className="sm:col-span-2 justify-self-start rounded-full bg-[#14110F] text-[#F6F2EC] px-9 py-4 text-[0.75rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#8A6A3A] disabled:opacity-60">
                      {loading ? "Creazione…" : "Crea il mio codice"}
                    </button>
                  </form>
                </Reveal>
              )}
            </div>
          </div>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative overflow-hidden aspect-[4/5] rounded-t-[240px]">
              <img src={IMG.team} alt="Il team di Gioia Centro Estetico" className="absolute inset-0 w-full h-full object-cover object-[50%_30%]" />
            </div>
          </Reveal>
        </div>
        <ol className="mt-24 sm:mt-32 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="referral-steps">
          {STEPS.map(([h, d], i) => (
            <motion.li key={h} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.9, delay: i * 0.08 }}
              className={`p-7 flex flex-col gap-10 ${i === 3 ? "bg-[#14110F] text-[#F6F2EC]" : "bg-[#ECE5DA] text-[#14110F]"}`}>
              <span className={`font-stencil tracking-[0.2em] ${i === 3 ? "text-[#C9A66B]" : "text-[#8A6A3A]"}`}>0{i + 1}</span>
              <div><h3 className="font-serif-d text-2xl">{h}</h3><p className={`mt-3 text-sm font-light leading-relaxed ${i === 3 ? "text-[#d9d0c4]" : "text-[#4f4841]"}`}>{d}</p></div>
            </motion.li>
          ))}
        </ol>
        <p className="mt-10 text-xs text-[#6b625a]">La sorpresa viene consegnata in istituto. Un codice per persona; valido per le amiche che prenotano per la prima volta.</p>
      </div>
    </PageShell>
  );
}
